import Cookies from 'js-cookie';
import AuthService from '@services/resources/AuthService';
import OAuthService from '@services/resources/OAuthService';
import PermissionsService from "@services/resources/PermissionsService";
import { setPending2FA, clearPending2FA } from '@utils/auth2faMemory';
import { redactPassword } from '@utils/auth2faHelpers';

const permissionService = PermissionsService.build();

export default {

    /**
     * Main Login Action
     */
    loginRequest(context, payload) {
        if (process.env.VUE_APP_NEW_AUTH_FLOW === 'true') {
            return context.dispatch('handleNewLoginFlow', payload);
        }

        const service = AuthService.build();
        return new Promise((resolve, reject) => {
            service.login(payload.username, payload.password)
                .then((resp) => {
                    let access_token = 'Bearer ' + resp.access_token;
                    Cookies.set('access_token', access_token, { expires: 365 });
                    context.commit('authSuccess', access_token);
                    context.dispatch('userRequest');
                    resolve(access_token);
                })
                .catch((err) => {
                    context.commit('authError', err);
                    Cookies.remove('access_token');
                    reject(err);
                });
        });
    },

    /**
     * Dedicated action for the New Flow
     * Isolated to avoid mixing complexity with legacy code.
     */
    handleNewLoginFlow({ commit, dispatch }, payload) {
        const service = OAuthService.build();

        return new Promise((resolve, reject) => {
            service.login(payload)
                .then((resp) => {
                    if (!resp || !resp.access_token || typeof resp.expires_in !== 'number') {
                        throw new Error('Resposta de autenticação malformada: campos obrigatórios ausentes ou inválidos.');
                    }

                    const accessToken = 'Bearer ' + resp.access_token;
                    const refreshToken = resp.refresh_token;

                    const expiresIn = resp.expires_in || 3600;
                    const fiveMinutes = 5 * 60 * 1000;
                    const expireAt = Date.now() + (expiresIn * 1000) - fiveMinutes;

                    // Cookie expiration strategy (INTENTIONAL - do not "fix"):
                    // - expire_at and email: session cookies (no expires) → forces revalidation when restarting the browser
                    // - access_token: expires: 1 day → safety margin of ~5min before the token's actual expiration
                    // - refresh_token: expires: 365 days → long-lived token for renewal
                    // See authRefresh.js for related renewal logic (expires: 7 days)
                    Cookies.set("expire_at", expireAt);
                    Cookies.set("email", payload.username);
                    Cookies.set('access_token', accessToken, { expires: 1 });

                    if (refreshToken) {
                        Cookies.set('refresh_token', refreshToken, { expires: 365 });
                    }

                    commit('authSuccess', accessToken);
                    dispatch('userRequest');
                    resolve(accessToken);
                })
                .catch((err) => {
                    commit('authError', err);
                    Cookies.remove('access_token');
                    Cookies.remove('refresh_token');
                    Cookies.remove('expire_at');
                    Cookies.remove("email");
                    reject(err);
                });
        });
    },

    /**
     * Main Logout Action
     */
    logoutRequest: ({ commit }) => {
        // --- NEW FLOW ---
        if (process.env.VUE_APP_NEW_AUTH_FLOW === 'true') {
            Cookies.remove('refresh_token');
            Cookies.remove("expire_at");
            Cookies.remove("email");
        }

        return new Promise((resolve) => {
            Cookies.remove('access_token');
            commit('authLogout');
            commit('userLogout');

            resolve(true);
        });
    },

    fetchAdmRoles({ commit }) {
        return permissionService.getAdmRoles()
            .then((data) => {
                if (!Array.isArray(data)) {
                    throw new Error(`fetchAdmRoles: Expected array, got ${typeof data}`);
                }
                commit('setAdmRoles', data);
                return data;
            })
            .catch((err) => {
                console.error('fetchAdmRoles error:', err);
                throw err;
            });
    },

    saveRefreshTokenForAccessCode({ commit }, refresh_token) {
        if (!refresh_token || typeof refresh_token !== 'string' || refresh_token.trim() === '') {
            console.warn('saveRefreshTokenForAccessCode: refresh_token inválido');
            return;
        }

        try {
            Cookies.set("refresh_token", refresh_token.trim(), { expires: 365 });
            commit("setRefreshToken", refresh_token);
        } catch (error) {
            console.warn('Erro ao salvar refresh_token:', error);
        }
    },

    saveLoginDataForAccessCode({ commit }, loginData) {
        if (!loginData || typeof loginData !== 'object') return;
        if (!loginData.username || !loginData.password) return;

        try {
            setPending2FA(loginData);

            const redactedData = redactPassword(loginData);
            commit("saveLoginData", redactedData);
        } catch (error) {
            console.error('Failed to secure 2FA data:', error);
        }
    },

    clearLoginDataForAccessCode({ commit }) {
        clearPending2FA();
        commit("clearLoginData");
    }
};
